import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import sendError from "../../utils/sendError";
import { ERROR_CODE_400 } from "manifest-bridge-core/src/errors";
import { useContext, useEffect } from "react";
import { AppContext } from "../../context/AppContext";
import { TransferContext } from "../../context/TransferContext";
import { BRIDGE_DEVELOPMENT, BRIDGE_PRODUCTION, BridgeEnv } from "../../typings";
import { sendAppVersionToSentry } from "../../lib/sentry";

const useLaunchController = () => {
  const { launchId, clientId } = useParams();
  const [searchParams] = useSearchParams();
  const { claimToken, setIsWebview } = useContext(AppContext);
  const { initBridge } = useContext(TransferContext);
  const navigate = useNavigate();

  if (!launchId) {
    const origin: string = searchParams.get("origin") || "*";
    sendError(
      {
        errorCode: ERROR_CODE_400,
        errorMessage: "Please pass launch token to launch bridge",
      },
      searchParams.get("webview") === "true",
      origin,
    );
    return;
  }

  useEffect(() => {
    const init = async () => {
      sendAppVersionToSentry();

      const env: BridgeEnv =
        searchParams.get("environment")?.toLowerCase() === "production"
          ? BRIDGE_PRODUCTION
          : BRIDGE_DEVELOPMENT || BRIDGE_DEVELOPMENT;
      const origin: string = searchParams.get("origin") || "*";
      if (!launchId || !clientId) return;
      const { $transferIntentId, $accessToken } = await claimToken({
        token: launchId,
        env,
        clientId,
        origin,
      });
      if (!$transferIntentId && !$accessToken && !clientId) return;

      const isInit = await initBridge({
        $transferIntentId,
        $accessToken,
        $clientId: clientId,
        env,
      });
      if (isInit) {
        setIsWebview(searchParams.get("webview") === "true");
        Sentry.setTag("webview", searchParams.get("webview") || "false");
        Sentry.setTag("clientEnv", searchParams.get("environment") || "development");
        navigate("/transfer");
      }
    };

    init();
  }, []);

  return {
    launchId,
  };
};

export default useLaunchController;
